/**
 * Three.js / WebGPU stage component (PR-A scaffolding + PR-B render pipeline).
 *
 * RP4 — orchestrator-only file. The bulk-load effect, frame loops, layer
 * visibility effect, selection/hover sync, mesh factories, teardown
 * helpers, and small placeholder helpers all live in `./scene/*` modules.
 * This file owns the React component tree, the prop forwarding, the
 * outer pointer/wheel/keyboard binding, and the Canvas / overlay layout.
 *
 * The Canvas uses `frameloop="demand"` so idle CPU stays at 0% — every
 * camera / entity / selection mutation invalidates the frame explicitly.
 * DPR is capped at `[1, 2]` to keep retina sharpness without doubling
 * pixel cost on 3x phones / unusual hi-DPI monitors.
 */

import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrthographicCamera } from "@react-three/drei";
import * as THREE from "three";
import type {
  Scene3DStageProps as Scene3DStagePropsContract,
  CoordinateSystem,
  StageCameraApi,
  ZoomObjectResult,
  ZoomOptions,
} from "./Scene3DStageProps";
// PR-V0b — camera state + tween + zoom-picking ref types.
import {
  type CameraStateSnapshot,
  CAMERA_TWEEN_DURATION_MS,
} from "./cameraApi";
import {
  CameraApiBinder,
  type PendingCameraTween,
} from "./cameraApiBinder";
import { type ZoomPickingState } from "./cameraApiAdapter";
import { useSysvarStore, selectEffectiveOrtho } from "../dynamicInput/sysvarStore";
import { usePolarDraftAidEffects } from "./scene/usePolarDraftAidEffects";
import { useOtrackAcquisitionEffects } from "./scene/useOtrackAcquisitionEffects";
import { useDraftGenerationRef } from "./scene/useDraftGenerationRef";
import { useOverlayE2EMirror } from "./scene/useOverlayE2EMirror";
import { useStageHoverStore } from "../state/hoverState";
// PR-G — HTML grip overlay layer (PR-K.1 split: camera tracker inside
// Canvas + DOM layer outside).
import {
  GripsCameraTracker,
  GripsDomLayer,
} from "./grips/gripsOverlay";
// GRIPS-INTERACTION PR-1/2 — the armed-grip model; the overlay reads it to
// paint the armed indicator + mode. The click-arm ↔ store bridge itself
// (onGripArm/onGripDisarm/onGripCycleMode) lives in `useGripStartBundle`.
import { getArmedGripSnapshot, subscribeHotGrips } from "./grips/hotGripStore";
// S12b — the live typed-entity resolver wiring the cad-core grip source into
// the overlay (kept out of gripsOverlay.tsx so that module stays store-free).
import { resolveTypedEntityById } from "./grips/typedGripResolver";
// PR-H — overlay layer.
import {
  Scene3DWorldOverlays,
  Scene3DDomOverlays,
  type OverlayState,
} from "./overlays";
// PR-V0c — draft preview rendering.
import { DraftPreviewLayer } from "./draft/draftPreviewLayer";
import { isDraftAnchored } from "./draft/draftAnchor";
// ORTHO anchor gating — the registry-driven rubber-band reference point (null
// for selection-window / scalar steps that must NOT be ortho-constrained).
// Filter-aware wrapper re-anchors ORTHO/POLAR onto an active point-filter's
// captured sub-point (FROM base / M2P first / ordinate supply) — Codex PR #504
// P2 — falling back to the host registry anchor otherwise.
import { orthoAnchorForDraftSessionWithFilter, pointFilterHasCapturedAnchor } from "../scene/pointFilterSession";
// N6 (POLAR-RELATIVE-BASIS-WIRE) — last-drawn-segment direction for
// POLARMODE's relative angle-measurement basis. Deliberately NOT the
// filter-aware ortho anchor: a point-filter sub-point is a construction aid,
// never "the last segment drawn" (see the helper's own doc).
import { polarReferenceAngleForDraftSession } from "../scene/draftSharedHelpers";
// OTRACK-SUBSYSTEM (local Codex review round 4 P2) — the registry-driven
// point-prompt classification (`draftSharedHelpers
// .draftSessionAcceptsPointPrompt`) combined with an exclusion for an active
// `TT` sub-prompt (which leaves `session.step` unchanged, so the registry
// alone cannot tell it apart from an ordinary point prompt). See the
// helper's own doc.
import { draftSessionAcceptsDwellAcquisition } from "../scene/otrackTrackPointSession";
// MC4 S4 — MODEL→PAPER mapping for an in-VP draft rubber band.
import { resolveDraftPreviewMapping } from "./draft/draftPreviewMapping";
// RP4 — extracted helpers / effects / mesh factories.
import {
  computeBoundsFromSpatialIndex,
  computeBoundsFromSpatialIndexForIds,
} from "./scene/scenePlaceholder";
import { PrintPreviewCameraBinder } from "./scene/PrintPreviewCameraBinder";
import { SceneContent } from "./scene/SceneContent";
import { bindStageInteractions } from "./scene/StageInteractionsBinder";
import type { RefinedEntityPicker } from "./scene/refinedEntityPick";
import { installWindowPickController } from "./scene/windowPickController";
import type { ViewportRedirectContext } from "./stageInteractions";
import { mountStageCameraApi } from "./scene/StageCameraApiAdapter";
import {
  buildBeditOverlayPatch,
  buildCursorSysvarOverlayPatch,
  applyPrintPreview,
  resolveStageCursor,
} from "./scene/StageOverlayState";
import {
  buildLookupGripClickAdapter,
  buildMleaderGripContextMenuAdapter,
} from "./scene/StageCallbackHelpers";
import {
  LiveTextOverlays,
  ArrayRectGripPlaceholders,
} from "./scene/StageDomChildren";
import { useStageRendererHandle } from "./scene/useStageRendererHandle";
import { useOsnapBindings } from "./scene/useOsnapBindings";
// PR-V0c / GRIPS-INTERACTION PR-1/2 — grip drag start handler + click-arm
// bridge (arm/disarm/mode-cycle/commit echo).
import { useGripStartBundle } from "./scene/useGripStartBundle";
// MC2 — PaperSpaceLayer + page-setup extraction.
import { PaperSpaceLayer } from "./scene/PaperSpaceLayer";
// MC3 — ViewportFrameLayer for VP border / lock / grip rendering.
import { ViewportFrameLayer } from "./scene/ViewportFrameLayer";
// MC4 — MSPACE_VIA_VP interactive mode (dblclick + Esc FSM + camera dive).
import {
  installViewportContextController,
  type SavedPaperState,
} from "./controllers/viewportContextController";
import { useSpaceContextStore } from "../state/spaceContextStore";
// MC3 — viewport render pass orchestration (P1 #1 runtime wiring).
import { ViewportCullingAdapter } from "./viewports/viewportCullingAdapter";
import {
  collectViewportLiftIds,
  createViewportLiftRequests,
  publishViewportLiftRequests,
  viewportLiftSignature,
  type ViewportLiftRequests,
} from "./viewports/viewportLiftRequests";
import { excludeControlViewport } from "./viewports/viewportControlFilter";
import { resolveRedirectViewportView } from "./viewports/redirectViewportView";
import { backgroundHexForSpaceKind } from "./rendering/layoutForeground";
import { renderViewportPass } from "./rendering/viewportRenderPass";
import { createViewportTraversalCacheStore } from "./rendering/viewportTraversalCaches";
import type { RenderableEntity, SpatialIndex } from "./types";
import {
  createSpaceLayerCensusCache,
  type SpaceLayerCensusCache,
} from "./scene/spaceLayerCensusCache";
import {
  extractPageSetup,
  defaultPageSetup,
  type ViewportEntity,
  type LayerDefinition,
} from "@dxf-ai-editor/drawing-model";
import { useEditorStore } from "../state/editorStore";
// Paper space joins the renderer's ONE (offset-world) frame: the sheet, the
// layout camera and every VP-rect consumer take the origin through
// `applyOffsetToPaperOrigin` instead of the raw `LayoutPageSetup.origin`.
// `getCoordinateOrigin` is read only as a memo dependency so the derived
// origin re-derives if the session origin locks after this component's first
// render (import locks it before the document merge today, but the memo must
// not silently cache a pre-lock identity conversion).
import {
  applyOffsetToPaperOrigin,
  getCoordinateOrigin,
} from "./coordinateOrigin";
import { computeLayoutFitCamera } from "./scene/layoutAutoFit";
import {
  EMPTY_LAYER_DEFS,
  computeVisibleLayers,
} from "./Scene3DStage.layerHelpers";

/**
 * MC3 — Per-viewport model-geometry render pass orchestrator.
 *
 * Uses `useFrame` with priority=1 to take over rendering control from R3F so
 * the viewport passes can composite model geometry into scissored VP windows
 * without the global model render leaking outside VP boundaries.
 *
 * Render sequence per frame:
 *   1. Pre-hide all model entity meshes (entityId / keyId userData).
 *   2. gl.render(scene, camera) — draws paper sheet + VP frame overlays only.
 *   3. Restore model entity mesh visibility.
 *   4. For each active viewport:
 *      a. Query ViewportCullingAdapter for visibleIds.
 *      b. Compute visibleLayers from visibleIds + spatial index.
 *      c. Call renderViewportPass — scissors + clips model geometry to VP rect.
 *
 * Must be mounted INSIDE <Canvas>. Unmount (by not rendering it) in model
 * space so R3F's auto-render takes over again.
 */
function ViewportRenderOrchestrator({
  viewports,
  paperOrigin,
  spatialIndexRef,
  renderSatellitesRef,
  renderSatellitesVersionRef,
  spaceLayerCensusRef,
  viewportLiftRequestsRef,
  geometryVersion,
  sheetSizeWld,
}: {
  viewports: ReadonlyArray<ViewportEntity>;
  paperOrigin: { x: number; y: number };
  spatialIndexRef: React.MutableRefObject<SpatialIndex | null>;
  /**
   * Codex PR #784 P1 — parent id → render-only satellites (the SAME map
   * `SceneContent`'s cull maintains as `multileaderBlockChildrenRef`). The
   * satellites are absent from the spatial index, so their layers are
   * projected from here into the VP layer sets
   * (`scene/satelliteLayerProjection.ts`).
   */
  renderSatellitesRef: React.MutableRefObject<Map<string, RenderableEntity[]>>;
  /** Revision of that map (bulk-load bumps it on clear / repopulate) — the census re-derives when it moves. */
  renderSatellitesVersionRef: React.MutableRefObject<number>;
  /**
   * Codex PR #784 round 3 P2 — the model / paper layer census, OWNED by the
   * stage (it outlives this orchestrator, which mounts only on a layout) and
   * shared with `SceneContent` → `FrameLoops`, whose relift path unions a
   * relifted parent's satellite layers into it (`scene/spaceLayerCensusCache.ts`).
   */
  spaceLayerCensusRef: React.MutableRefObject<SpaceLayerCensusCache>;
  /**
   * Codex PR #784 round-9 P1 (`lod/lodPolicy.ts:225`) — stage-owned record this
   * orchestrator PUBLISHES into: the union of the ids each viewport needs
   * materialised at its OWN model scale. `FrameLoops` (via `SceneContent`)
   * unions it into the newly-visible diff, so a block that is sub-pixel under
   * the model-wide fit but large inside a viewport takes the same on-demand
   * lift the main camera drives, instead of leaving the viewport blank because
   * `query` / `renderViewportPass` only ever compute ids and mask meshes that
   * already exist. See `viewports/viewportLiftRequests.ts`.
   */
  viewportLiftRequestsRef: React.MutableRefObject<ViewportLiftRequests>;
  /** Incremented on every entity mutation; triggers cull-cache invalidation (#7). */
  geometryVersion: number;
  /** Paper sheet world-unit dimensions for direct GL white fill. */
  sheetSizeWld: { w: number; h: number };
}): null {
  const cullingAdapterRef = useRef<ViewportCullingAdapter | null>(null);
  const prevSpatialRef = useRef<SpatialIndex | null>(null);
  // Codex PR #784 round-9 P1 — cached change signature of the published lift
  // request set (`viewports/viewportLiftRequests.ts viewportLiftSignature`).
  // The VP pass runs every frame; recomputing the set only when this string
  // moves is what keeps an idle layout at one small string build per frame and
  // leaves `FrameLoops` on its camera-delta early-out.
  const liftSignatureRef = useRef<string | null>(null);
  // Layers with ≥1 model-space entity and layers with ≥1 paper-space entity.
  // Both are needed by the UNTAGGED-mesh fallback: hide a keyId bucket only
  // when its layer is model-ONLY (fix #5 — avoid hiding a bucket that contains
  // paper content). Tagged buckets no longer need it — Codex round 15 P1 made
  // the space part of the bucket key (`batchedLayer.ts MeshKey.space`), so a
  // bucket is wholly model or wholly paper and its own tag decides.
  // Cached on (spatial identity, geometryVersion, satellite-map revision) and
  // grown in place by the FrameLoops relift — `scene/spaceLayerCensusCache.ts`;
  // the ref is the stage-owned prop (see the props JSDoc).
  //
  // Scene-traversal caches — the step-1 pre-hide set, the step-5 frame-only
  // hide set and the overlay roots. `scene.traverse` is O(#objects), so they
  // are rebuilt only when the census moved, when the scene ROOT gained or lost
  // objects (Codex PR #784 round-11 P1 — a relift materialises bucket / hatch /
  // text / arrow / array objects without touching any census key) or when a
  // cached object was detached from a nested parent. Contract + rationale:
  // `rendering/viewportTraversalCaches.ts`.
  const traversalCachesRef = useRef(createViewportTraversalCacheStore());

  useFrame(({ gl, scene, camera, size, invalidate }) => {
    const spatial = spatialIndexRef.current;

    // Rebuild culling adapter + model-layer set when the spatial index is replaced
    // (new DXF import) OR when geometry mutated (fix #3 — incremental edits keep
    // the same spatial index instance so the ref-equality guard alone misses them).
    if (spatial !== prevSpatialRef.current) {
      cullingAdapterRef.current = spatial
        ? new ViewportCullingAdapter(spatial)
        : null;
      prevSpatialRef.current = spatial;
    }
    // Recompute model/paper-layer sets for keyId bucket discrimination when
    // the spatial index, the geometry version OR the satellite-map revision
    // moved (Codex PR #784 round 3 P1 #2 — the async cache-direct lift
    // repopulates the satellite map AFTER the first paper-space frame may
    // have cached the census; `scene/spaceLayerCensusCache.ts`). The
    // scene-traversal caches below key on the census → invalidated with it.
    const censusChanged = spaceLayerCensusRef.current.sync({
      spatial,
      geometryVersion,
      satellitesVersion: renderSatellitesVersionRef.current,
      satellitesByParent: renderSatellitesRef.current,
    });

    // 1. Pre-hide model-space entity meshes so the main render shows only
    //    paper sheet + VP frame overlays (not globally-rendered model geo).
    //    Also collect paper-space overlay root groups (PaperSpaceLayer,
    //    ViewportFrameLayer) tagged with isViewportOverlay=true so they can be
    //    hidden during per-VP passes (Codex P1 iter-5 fix), and ALL
    //    entity-tagged objects for the step-5 frame-only hide.
    //
    //    The classification + its invalidation keys (census move, scene-root
    //    topology change, detached cached object) live in
    //    `rendering/viewportTraversalCaches.ts`; steady state costs no
    //    traversal.
    const { preHidable, allEntity, overlays: overlayObjects } =
      traversalCachesRef.current.sync({
        scene,
        spatial,
        census: spaceLayerCensusRef.current,
        censusChanged,
      });

    // Hide only objects that are currently visible (they will be restored later).
    const preHidden: THREE.Object3D[] = [];
    for (const obj of preHidable) {
      if (obj.visible) {
        obj.visible = false;
        preHidden.push(obj);
      }
    }

    // 2. Direct WebGL clear + white paper sheet fill.
    //
    // Root cause (GAP2 iter-11 VPDIAG-S): gl.render(scene, camera) with the
    // white PaperSpaceLayer MeshBasicMaterial produces ZERO white pixels after
    // step 2 — despite hasPaper=true, overlays=2. The Three.js mesh path does
    // not reliably write pixels in this priority=1 useFrame context (likely a
    // render-target state interaction with preserveDrawingBuffer:true).
    //
    // Fix: bypass the Three.js mesh entirely. Use raw WebGL:
    //   (a) glCtx.clear — transparent full-canvas clear
    //   (b) scissor to paper sheet bounds → glCtx.clearColor(1,1,1,1) → glCtx.clear
    // This guarantees exactly the right pixels are white regardless of Three.js
    // internal state. The step-5 frame-on-top pass composites the VP border.
    const glCtx = gl.getContext() as WebGLRenderingContext;
    const dpr = gl.getPixelRatio();
    const physW = Math.round(size.width * dpr);
    const physH = Math.round(size.height * dpr);

    glCtx.clearColor(0, 0, 0, 0);
    glCtx.clear(glCtx.COLOR_BUFFER_BIT | glCtx.DEPTH_BUFFER_BIT);

    const _sv = new THREE.Vector3();
    const projectToPhys = (wx: number, wy: number): { x: number; y: number } => {
      _sv.set(wx, wy, 0);
      _sv.project(camera);
      return {
        x: Math.round((_sv.x + 1) / 2 * physW),
        y: Math.round((_sv.y + 1) / 2 * physH),
      };
    };
    const ll = projectToPhys(paperOrigin.x, paperOrigin.y);
    const ur = projectToPhys(paperOrigin.x + sheetSizeWld.w, paperOrigin.y + sheetSizeWld.h);
    const sx = Math.max(0, Math.min(ll.x, ur.x));
    const sy = Math.max(0, Math.min(ll.y, ur.y));
    const sw = Math.min(physW, Math.max(ll.x, ur.x)) - sx;
    const sh = Math.min(physH, Math.max(ll.y, ur.y)) - sy;
    if (sw > 0 && sh > 0) {
      glCtx.enable(glCtx.SCISSOR_TEST);
      glCtx.scissor(sx, sy, sw, sh);
      glCtx.clearColor(1, 1, 1, 1);
      glCtx.clear(glCtx.COLOR_BUFFER_BIT);
      glCtx.disable(glCtx.SCISSOR_TEST);
      glCtx.clearColor(0, 0, 0, 0); // sync back to Three.js default transparent
    }

    // 2b. Render paper-space entities (title blocks, dimensions, text, lines
    // authored in paper space) over the white GL fill.
    //
    // Model meshes are already in preHidden (hidden). The PaperSpaceLayer
    // white-sheet overlay is hidden here because the GL fill already painted
    // white — re-rendering the Three.js mesh would cause a depth/alpha
    // interaction with preserveDrawingBuffer:true. ViewportFrameLayer stays
    // visible so VP borders composite correctly (step 4 scissored clears will
    // erase them, step 5 re-draws them on top).
    {
      const paperSheetOverlays: THREE.Object3D[] = [];
      for (const obj of overlayObjects) {
        if ((obj.userData as Record<string, unknown>)["isViewportFrame"] !== true) {
          obj.visible = false;
          paperSheetOverlays.push(obj);
        }
      }
      gl.autoClear = false;
      try {
        gl.render(scene, camera);
      } finally {
        gl.autoClear = true;
        for (const obj of paperSheetOverlays) obj.visible = true;
      }
    }

    // 3. Restore model entity visibility for the per-VP passes.
    for (const obj of preHidden) obj.visible = true;

    // 4. Per-viewport scissored model-geometry passes.
    // MC3 fix-7 — Sort DESCENDING (larger status first): DXF stacking is
    // inverse to status value (status 1 = active/top, larger = below).
    // Painter's algorithm: draw lower-priority (larger status) first so
    // status-1 (highest priority) is drawn last and appears on top.
    //
    // MC4 S3 — empty viewport list is a valid, safe state: a layout with ZERO
    // viewports must render a blank sheet (steps 1-3 above already hid model +
    // painted the white sheet). This early-return makes the per-VP loop a
    // no-op WITHOUT touching model visibility again — step 3 has already
    // restored it and no further gl.render runs, so the on-screen buffer is
    // the step-2b paper-sheet render (model hidden). No divide-by-zero / NaN /
    // crash: sortedViewports + adapter.query are never reached with 0 VPs.
    const adapter = cullingAdapterRef.current;

    // Codex PR #784 round-9 P1 (`lod/lodPolicy.ts:225`) — publish what these
    // viewports need LIFTED, measured at each viewport's own model scale.
    //
    // This orchestrator can only mask meshes that already exist
    // (`applySceneVisibilityMask` / `renderViewportPass`), and the bulk-load LOD
    // pre-classify drops a stub by its size under the MODEL-WIDE fit — so a
    // reference that is sub-pixel there but metres wide inside a viewport had no
    // bucket slot and no direct mesh, and the viewport rendered it blank. The
    // lift itself stays in `FrameLoops` (one seam: `entityResolverRef` publish +
    // `emitReliftDirectMeshes` + `addEntity`); this only names the ids and wakes
    // a frame. Runs BEFORE the zero-viewport early-out so leaving a layout, or
    // deleting its last viewport, clears the set instead of pinning geometry.
    {
      const mainZoom = (camera as THREE.OrthographicCamera).zoom;
      const signature =
        adapter && spatial
          ? viewportLiftSignature({
              viewports,
              mainCameraZoom: mainZoom,
              geometryVersion,
              bulkLoadVersion: renderSatellitesVersionRef.current,
            })
          : "";
      if (signature !== liftSignatureRef.current) {
        liftSignatureRef.current = signature;
        const next =
          adapter && spatial
            ? collectViewportLiftIds({
                viewports,
                queryVisibleIds: (vp) => adapter.query(vp),
                getBounds: (id) => spatial.getBounds(id),
                mainCameraZoom: mainZoom,
              })
            : new Set<string>();
        if (
          publishViewportLiftRequests({
            requests: viewportLiftRequestsRef.current,
            next,
          })
        ) {
          // `FrameLoops` runs at priority 0 — BEFORE this pass — so it acts on
          // the new version on the NEXT frame. Under `frameloop="demand"` that
          // frame is not guaranteed to happen on its own; ask for it.
          invalidate();
        }
      }
    }

    if (!adapter || !spatial || viewports.length === 0) return;

    const mainCamera = camera as THREE.OrthographicCamera;
    const sortedViewports = viewports
      .slice()
      .sort((a, b) => b.viewportStatus - a.viewportStatus);

    // Hide paper-space overlays during VP passes so PaperSpaceLayer /
    // ViewportFrameLayer geometry does not render through each VP camera
    // (Codex P1 iter-5 fix — overlay bleed into model-space viewport windows).
    for (const obj of overlayObjects) obj.visible = false;
    try {
      for (const viewport of sortedViewports) {
        const visibleIds = adapter.query(viewport);
        const visibleLayers = computeVisibleLayers(
          visibleIds,
          spatial,
          renderSatellitesRef.current,
        );
        renderViewportPass({
          gl,
          mainCamera,
          modelScene: scene,
          viewport,
          visibleIds,
          visibleLayers,
          modelLayers: spaceLayerCensusRef.current.modelLayers,
          canvasWidth: size.width,
          canvasHeight: size.height,
          paperOrigin,
        });
      }
    } finally {
      // Restore overlay visibility after all VP passes (always — even on throw).
      for (const obj of overlayObjects) obj.visible = true;
    }

    // 5. Final frame-on-top pass: re-draw the VP frame overlay above all
    //    composited VP model-geometry passes.
    //
    //    The frame was correctly painted in step 2, but each per-VP pass in
    //    step 4 calls ctx.clear(COLOR_BUFFER_BIT) inside a scissored rect that
    //    covers the VP boundary — erasing exactly the frame pixels that sat
    //    there.  After step 4, the frame group is visible in the scene graph
    //    but no render call has redrawn it → 0 visible pixels on screen.
    //
    //    Fix: composite the frame group alone (isViewportFrame:true) on top of
    //    the full canvas with autoClear=false.  GL state after the last
    //    popWebGLState is: scissor disabled, full-canvas viewport, autoClear=true
    //    — so we only need to flip autoClear and temporarily isolate the frame.
    const frameGroup = overlayObjects.find(
      (o) => (o.userData as Record<string, unknown>)["isViewportFrame"] === true,
    );
    if (frameGroup) {
      // P1 fix: hide ALL non-frame scene content — including PAPER buckets and
      // any untagged keyId bucket that `preHidden` deliberately excluded. Use
      // the cached `allEntity` set (built by
      // `rendering/viewportTraversalCaches.ts` above) instead of a full
      // scene.traverse every frame.
      const step5Hidden: THREE.Object3D[] = [];
      for (const obj of allEntity) {
        if (!obj.visible || obj.parent === null) continue;
        obj.visible = false;
        step5Hidden.push(obj);
      }
      // Hide non-frame overlays (PaperSpaceLayer white sheet would overdraw VP).
      for (const obj of overlayObjects) {
        if (obj !== frameGroup) obj.visible = false;
      }
      // Composite the frame over the full canvas without clearing color, but
      // clear depth so lock badges + grip markers (depthTest:true) always
      // composite on top of model geometry written during the per-VP passes.
      // Without this, depth values from renderViewportPass remain active and
      // occlude frame-group children at model geometry positions.
      // P2 fix: try/finally guarantees visibility is always restored even if
      // gl.render throws, preventing a permanently corrupted scene graph state.
      gl.autoClear = false;
      glCtx.clear(glCtx.DEPTH_BUFFER_BIT);
      try {
        gl.render(scene, camera);
      } finally {
        gl.autoClear = true;
        // Restore full scene visibility.
        for (const obj of overlayObjects) obj.visible = true;
        for (const obj of step5Hidden) obj.visible = true;
      }
    }
  }, 1); // priority=1 → manual render control (disables R3F auto-render)

  // Invalidate culling cache when the viewport list contents OR geometry version
  // changes (#7 — model edits update geometryVersion so stale VP cull results
  // are discarded even when VP params are unchanged).
  useEffect(() => {
    cullingAdapterRef.current?.invalidate();
  }, [viewports, geometryVersion]);

  // Codex PR #784 round-9 P1 — the request record is stage-owned and outlives
  // this layout-only mount, so drop the ids on the way out. Leaving them behind
  // would keep `FrameLoops` pinning viewport geometry lifted in model space,
  // where no viewport is on screen to justify it.
  useEffect(() => {
    const requestsRef = viewportLiftRequestsRef;
    return () => {
      liftSignatureRef.current = null;
      publishViewportLiftRequests({
        requests: requestsRef.current,
        next: new Set<string>(),
      });
    };
  }, [viewportLiftRequestsRef]);

  return null;
}

/**
 * B1 fix (post-V9): one-shot initial camera position setter.
 *
 * Apply the (0, 0, 100) seed exactly once via `useLayoutEffect`. The
 * subsequent fit-view path inside `SceneContent.bulkLoad` then writes
 * the actual bbox centre, and no React reconciliation overrides it.
 *
 * Mounted as a sibling of `<OrthographicCamera>` so it can read the
 * default camera through `useThree`. Returns `null` — contributes no
 * render output.
 */
function InitialCameraMount({
  cameraRef,
}: {
  cameraRef: React.MutableRefObject<THREE.OrthographicCamera | null>;
}): null {
  const camera = useThree((s) => s.camera);
  const size = useThree((s) => s.size);
  // Manual mode: drei would re-assert zoom={1} every render and reset wheel-zoom state.
  // Init effect — seed position + zoom exactly once per camera (resize must NOT clobber user pan/zoom).
  React.useLayoutEffect(() => {
    const ortho = camera as THREE.OrthographicCamera;
    if (!ortho.isOrthographicCamera) return;
    ortho.position.set(0, 0, 100);
    ortho.zoom = 1;
    ortho.updateMatrixWorld();
    ortho.updateProjectionMatrix();
    cameraRef.current = ortho;
    (window as unknown as { __cam?: THREE.OrthographicCamera }).__cam = ortho;
  }, [camera, cameraRef]);
  // Frustum tracks viewport since drei no longer auto-syncs it.
  React.useLayoutEffect(() => {
    const ortho = camera as THREE.OrthographicCamera;
    if (!ortho.isOrthographicCamera) return;
    ortho.left = -size.width / 2;
    ortho.right = size.width / 2;
    ortho.top = size.height / 2;
    ortho.bottom = -size.height / 2;
    ortho.updateProjectionMatrix();
  }, [camera, size.width, size.height]);
  return null;
}

/**
 * Re-export the prop contract from the dedicated types module so
 * existing consumers that imported `Scene3DStageProps` from this file
 * keep compiling.
 */
export type Scene3DStageProps = Scene3DStagePropsContract;

export type {
  CoordinateSystem,
  StageCameraApi,
  ZoomObjectResult,
  ZoomOptions,
};

// RP4 — re-export bounds helpers (consumed by App.tsx + tests).
export {
  computeBoundsFromSpatialIndex,
  computeBoundsFromSpatialIndexForIds,
};

/**
 * Bare R3F shell for the new renderer. PR-B mounts a SceneContent
 * child inside `<Canvas>` so the rendering effect can access THREE
 * scene / camera / gl through `useThree`. The outer component still
 * owns prop forwarding + the test bridge handle.
 */
export function Scene3DStage(props: Scene3DStageProps): React.ReactElement {
  // Defeat unused-prop warnings without losing the contract: every
  // prop is accepted but not yet wired to renderer logic.
  void props.blockDefinitions;
  // @codex #334 (P2-B) — `props.textStyles` is now wired through
  // `<SceneContent>` → `useSceneEffects` → `runSceneBulkLoad` →
  // `emitTextMeshes` so a generically-named STYLE (`name=Standard,
  // fontName=gost.shx`) dispatches text on its actual font file.
  void props.resetToken;
  void props.statusText;
  void props.onEntityDoubleClick;
  void props.coordinateSystem;
  void props.onStageText;
  void props.onOpenDynInputSettings;
  // S10d — cannoscaleName / annoAllVisible / annotationScales are wired below.
  void props.annoAutoScale;
  void props.annoMonitor;
  void props.onOpenScaleListEditDialog;
  void props.blockRecords;

  // Camera + canvas refs shared with `<InitialCameraMount>` / `<Canvas
  // onCreated>` (inside Canvas) and `useStageRendererHandle` (outside),
  // which builds the test-bridge projection from both. Null until R3F
  // finishes mounting.
  const sceneCameraRef = useRef<THREE.OrthographicCamera | null>(null);
  const sceneCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // The renderer handle holds the real spatial index that
  // `__edit_dwg_scene` exposes. The handle survives across re-renders;
  // entity content is refreshed by SceneContent's effect.
  const { handleRef, batchedLayerRef } = useStageRendererHandle({
    cameraRef: sceneCameraRef,
    canvasRef: sceneCanvasRef,
  });

  // PR-H — cursor tracking + overlay state.
  const overlayShellRef = useRef<HTMLDivElement | null>(null);
  const [overlayState, setOverlayState] = React.useState<OverlayState>({
    crosshairVisible: false,
  });
  // PR-V0c — shared locked-layer set. Maintained by SceneContent's
  // layerVisibility effect; consumed by GripsDomLayer.
  const lockedLayersRef = useRef<Set<string>>(new Set());
  // Codex PR #784 round 4 F2 — shared GLOBAL off / frozen layer set, ADOPTED by
  // SceneContent's ref bundle (`useSceneContentRefs.sharedHiddenLayersRef`) so
  // this is the exact ref the layer-visibility effect republishes and the click
  // path reads. Handed to `bindStageInteractions` so the pointer-move hover
  // hit-test applies the same INSERT child-geometry filter as the click pick.
  const hiddenLayersRef = useRef<Set<string>>(new Set());
  const isDrafting = props.draftSession != null;
  // MC4 edge case #2 — live "draft in progress" flag for the (once-installed)
  // stage pointer handlers, refreshed each render so handlers read it fresh
  // WITHOUT re-installing listeners mid-draft. When true, onPointerMove
  // resolves the input space ungated so the cursor maps into the active VP
  // MODEL space past the VP edge — the rubber band spans the frame and the
  // render-side clip trims it (AutoCAD floating-MSPACE parity). Stable ref
  // identity — intentionally NOT a binder-effect dependency.
  // Codex P2 (#234): gate on the draft having an ANCHOR (≥1 committed
  // point), NOT merely armed — else the FIRST point could be placed from
  // outside the VP frame. AutoCAD only allows drawing PAST the frame once
  // an anchor exists inside it. A captured point-filter sub-point counts
  // too, even while `points` is still empty. (Codex PR #504 P2.)
  const draftAnchored =
    isDraftAnchored(props.draftSession) ||
    Boolean(props.draftSession && pointFilterHasCapturedAnchor(props.draftSession));
  const draftActiveRef = useRef(draftAnchored);
  draftActiveRef.current = draftAnchored;
  // OTRACK-SUBSYSTEM — "active point prompt, excluding an active TT
  // sub-prompt" signal (see `StageInteractionBindings
  // .draftPointPromptActiveRef`'s doc; local Codex review round 3 P2
  // narrowed this from the broader `isDrafting`, which stayed true through
  // non-point sub-prompts like PLINE's width entry; round 4 P2 further
  // excludes an active `TT` sub-prompt — see
  // `draftSessionAcceptsDwellAcquisition`'s doc).
  const draftAcceptsPointInput = draftSessionAcceptsDwellAcquisition(props.draftSession ?? null);
  const draftPointPromptActiveRef = useRef(draftAcceptsPointInput);
  draftPointPromptActiveRef.current = draftAcceptsPointInput;
  // OTRACK-SUBSYSTEM round 6 P2 — see `useDraftGenerationRef`'s own doc.
  const draftGenerationRef = useDraftGenerationRef(props.draftSession);
  // ORTHO-constraint (AutoCAD ORTHOMODE / F8) — the draft rubber-band ANCHOR,
  // in the draft session's own coordinate space (model coords in MSPACE_VIA_VP,
  // layout coords over paper — the same space the pointer resolves to, so the
  // 0/90° lock is coordinate-consistent). Refreshed each render like
  // `draftActiveRef` (stable ref object, read at event-time by the once-installed
  // handlers, so NOT a binder-effect dep). `orthoLockedCursor` reads the live
  // ORTHOMODE / Shift temp-override via the sysvar store.
  //
  // @codex P2 — the anchor is the registry-driven rubber-band reference point
  // (`orthoAnchorForDraftSession`, via the filter-aware wrapper that re-anchors
  // onto an active point-filter's captured sub-point — Codex PR #504 P2), NOT a
  // raw `points.at(-1)`. That helper
  // resolves the step's ORTHO `$LASTPOINT`: it defaults to the typed-input
  // reference but honours the registry's explicit `orthoReference` for steps
  // whose preview rubber-bands from a prior point while their typed input is
  // not a relative coordinate — `spline-fit-next` (last fit point) and
  // `arc-sce-center` (start point). So ortho engages for EVERY point-prompt
  // that genuinely draws the next pick relative to a prior point (fixing the
  // SPLINE fit points after the first, which were diagonal @codex P2), yet a
  // raw last-point anchor is still avoided: SELECTION-window steps whose stored
  // point is not a drafting reference stay `"none"` — e.g. `stretch-select-second`
  // stores the first crossing-window corner, and forcing the second corner onto
  // the same axis collapses the window to zero area (no stretch selection). It
  // also excludes the FIRST point of every command (empty points → null), so the
  // first pick is never ortho-constrained, matching AutoCAD. The picked base is
  // the same rubber-band origin the preview and typed-relative input use, so the
  // ortho lock stays visually consistent with the rubber band.
  const orthoAnchor = props.draftSession
    ? orthoAnchorForDraftSessionWithFilter(props.draftSession)
    : null;
  const orthoBasePointRef = useRef<{ x: number; y: number } | null>(orthoAnchor);
  orthoBasePointRef.current = orthoAnchor;
  // @codex P2 — a primitive key for the anchor so the ORTHO-stale-cache effect
  // below can re-resolve the cached snap when the anchor APPEARS or MOVES with
  // no pointer move (e.g. the first LINE point / base typed as a coordinate
  // while the cursor already rests on the canvas). Without it `lastSnappedWorld`
  // stays the pre-anchor RAW cursor and the next click commits a diagonal point
  // instead of the 0/90°-locked one. `null` when there is no anchor.
  const orthoAnchorKey = orthoAnchor
    ? `${orthoAnchor.x},${orthoAnchor.y}`
    : null;
  // N6 (POLAR-RELATIVE-BASIS-WIRE) — last-drawn-segment direction, refreshed
  // each render like `orthoBasePointRef` above. `null` = no prior segment
  // (degrades to the same 0° rotation as absolute — see the helper's doc).
  const polarReferenceAngleDeg = props.draftSession
    ? polarReferenceAngleForDraftSession(props.draftSession)
    : null;
  const polarReferenceAngleRef = useRef<number | null>(polarReferenceAngleDeg);
  polarReferenceAngleRef.current = polarReferenceAngleDeg;
  // Primitive re-key, same rationale as `orthoAnchorKey` — the POLAR-STALE-
  // CACHE effect below re-resolves the cached snap when the reference angle
  // itself changes (a new point committed) with no pointer move.
  const polarReferenceAngleKey =
    polarReferenceAngleDeg === null ? null : String(polarReferenceAngleDeg);
  // MC4 iter-4 P2 #165: moved up from below so the hover gate can read
  // spaceContextMode without a temporal dead zone (the gate uses it in its
  // isHoverInActiveSpace closure via a ref). All later users of
  // spaceContextMode still work — value is computed once per render.
  const spaceContextMode = useSpaceContextStore((s) => s.mode);
  const spaceContextModeRef = useRef(spaceContextMode);
  spaceContextModeRef.current = spaceContextMode;
  // Hover-over-entity → OS `pointer` (hand) cursor, matching web
  // affordance for selectable items. Subscribes to the standalone
  // hover store so cursor changes don't re-render the whole tree.
  //
  // Gate by active space: `useStageHoverStore` writes carry the
  // `spaceId` snapshot from when the write happened; on a layout
  // switch the previous space's stale entityId would otherwise keep
  // the cursor pinned to "pointer" until the next pointermove in the
  // new space rewrites the store. Per Codex review on PR #50.
  const activeSpaceIdForHover = props.activeSpace?.id ?? "model";
  const activeSpaceIdForHoverRef = useRef(activeSpaceIdForHover);
  activeSpaceIdForHoverRef.current = activeSpaceIdForHover;
  // MC4 iter-4 P2 #165: also accept model-space hover writes in
  // MSPACE_VIA_VP — effectiveInteractionSpaceId routes to "model" so
  // hover store writes carry spaceId="model", but activeSpaceIdForHoverRef
  // still holds the layout id. Without this check the hand cursor and
  // snap preview never activate while editing model-through-VP.
  const isHoverInActiveSpace = (state: {
    entityId: string | null;
    spaceId: string | null;
  }): boolean =>
    state.entityId !== null &&
    (state.spaceId === activeSpaceIdForHoverRef.current ||
      (spaceContextModeRef.current === "MSPACE_VIA_VP" &&
        state.spaceId === "model"));
  const [hoveringEntity, setHoveringEntity] = React.useState<boolean>(() =>
    isHoverInActiveSpace(useStageHoverStore.getState()),
  );
  // Re-evaluate when the active space or VP mode changes — a stale entityId
  // from the previous space must not leak a hand cursor into the new space.
  useEffect(() => {
    setHoveringEntity(isHoverInActiveSpace(useStageHoverStore.getState()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSpaceIdForHover, spaceContextMode]);
  useEffect(() => {
    setHoveringEntity(isHoverInActiveSpace(useStageHoverStore.getState()));
    return useStageHoverStore.subscribe((state, prev) => {
      const next = isHoverInActiveSpace(state);
      const before = isHoverInActiveSpace(prev);
      if (next === before) return;
      setHoveringEntity(next);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Codex P3 re-audit — armed-grip cursor priority; see `resolveStageCursor`.
  const armedGripForCursor = useSyncExternalStore(subscribeHotGrips, getArmedGripSnapshot, getArmedGripSnapshot);
  useEffect(() => {
    const el = overlayShellRef.current;
    if (!el) return;
    el.style.cursor = resolveStageCursor({
      armed: armedGripForCursor !== null,
      hoveringEntity,
      crosshairVisible: overlayState.crosshairVisible ?? false,
    });
  }, [overlayState.crosshairVisible, hoveringEntity, armedGripForCursor]);
  // MC4 S3 / OTRACK-SUBSYSTEM — E2E observability mirror, extracted to
  // `useOverlayE2EMirror.ts` (this file is at its 2000-line cap).
  useOverlayE2EMirror(overlayState);
  // Plumb idle-selection-mode (no active draft command) into overlay
  // state so the PickboxCursor sprite knows when to render.
  useEffect(() => {
    setOverlayState((prev) =>
      prev.idleSelectionMode === !isDrafting
        ? prev
        : { ...prev, idleSelectionMode: !isDrafting },
    );
  }, [isDrafting]);
  // MC4 clip-debug — read-only E2E mirror of the live draft-session state onto
  // `window.__edit_dwg_draftsession` (no production code reads it; same
  // legitimate hook pattern as `__edit_dwg_draftpreview` / `__cam`). Lets a
  // headless gate confirm whether the FIRST point is committed (`hasFirst`) and
  // whether a hover has been recorded (`hasHover`) for the session that drives
  // `draftActiveRef`, so a null rubber band can be traced to its real cause.
  //
  // VPCLIP-INTERACTIVE (W2.6) — added `command`/`step`/`options` so a headless
  // scenario can confirm the multi-step select-viewport → first-corner →
  // second-corner progression by STEP NAME (not just point count), since
  // several other multi-step commands also carry exactly one committed point
  // mid-flow and `hasFirst`/`hasHover` alone cannot disambiguate which step
  // that point belongs to.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const session = props.draftSession;
    (
      window as unknown as {
        __edit_dwg_draftsession?: {
          spaceId: string;
          hasFirst: boolean;
          hasHover: boolean;
          command: string;
          step: string;
          options: Record<string, string>;
        } | null;
      }
    ).__edit_dwg_draftsession = session
      ? {
          spaceId: session.spaceId,
          hasFirst: session.points.length > 0,
          hasHover: session.hoverPoint != null,
          command: session.command,
          step: session.step,
          options: session.options,
        }
      : null;
  }, [props.draftSession]);
  // PR-V0b — camera state refs shared with `cameraApi.ts`.
  const cameraStateRef = useRef<CameraStateSnapshot>({
    centerX: 0,
    centerY: 0,
    zoom: 1,
  });
  const viewportSizeRef = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const pendingTweenRef = useRef<PendingCameraTween | null>(null);
  // MC4 Fix #4 — persists paper-camera snapshot across effect-driven controller
  // reinstalls (activeLayoutViewports identity change loses closure-local saved).
  const vpControllerSavedRef = useRef<SavedPaperState | null>(null);
  // MC4 iter-7 P2 #6 — persists exit-tween reentry guard across controller
  // reinstalls (same pattern as vpControllerSavedRef / Fix #4).
  const vpExitTweenRef = useRef<boolean>(false);
  // Forward refs the binder + interactions hook need for picking.
  const liveCameraRef = useRef<THREE.Camera | null>(null);
  const spatialIndexRefForHover = useRef<
    import("./types").SpatialIndex | null
  >(null);
  // Codex PR #784 round 3 P3 — the grips overlay's locked-layer gate reads a
  // selected entity's block-reference layer chain off its RENDERABLE (a
  // legacy-branch block child under an INSERT on a locked layer gets no
  // grips). Stable identity: it reads the ref at call time.
  const resolveGripRenderable = useCallback(
    (id: string) => spatialIndexRefForHover.current?.resolve(id) ?? null,
    [],
  );
  // Codex PR #784 P1 — parent id → render-only satellites, OWNED here and
  // adopted by `SceneContent` (→ `useSceneContentRefs` as
  // `multileaderBlockChildrenRef`) so the paper-space viewport orchestrator
  // below reads the exact map the cull maintains.
  const renderSatellitesRef = useRef<Map<string, RenderableEntity[]>>(
    new Map(),
  );
  // Codex PR #784 round 3 P1 #2 — revision of that map (bumped by the
  // bulk-load) so the orchestrator's layer census follows the async lift.
  const renderSatellitesVersionRef = useRef<number>(0);
  // Codex PR #784 round 3 P2 — the model / paper layer census the layout
  // orchestrator gates on, owned here so it survives the orchestrator's
  // layout-only mount AND is reachable from `SceneContent` → `FrameLoops`,
  // whose relift path unions relifted satellite layers into it.
  const spaceLayerCensusRef = useRef<SpaceLayerCensusCache>(
    createSpaceLayerCensusCache(),
  );
  // Codex PR #784 round-9 P1 (`lod/lodPolicy.ts:225`) — ids the ACTIVE
  // PAPER-SPACE VIEWPORTS need materialised, measured at each viewport's OWN
  // model scale. Published by `ViewportRenderOrchestrator` below (the only
  // scope that knows the viewports + the main camera) and consumed by
  // `SceneContent` → `FrameLoops`, which unions it into the newly-visible diff
  // so those ids take the SAME on-demand lift the main camera drives
  // (`viewports/viewportLiftRequests.ts`). Owned HERE because it must outlive
  // the orchestrator's layout-only mount — leaving a stale set behind on the
  // way back to model space would pin geometry no camera needs.
  const viewportLiftRequestsRef = useRef<ViewportLiftRequests>(
    createViewportLiftRequests(),
  );
  // Codex PR #784 round 7 P2 — the REFINED entity picker, OWNED here and
  // published by `SceneContent`'s `installStageClickHandlers` (which alone can
  // see the `VisibleSetCache` / typed resolver / adapter context the
  // cache-direct lift needs). Handed to `bindStageInteractions` so `ZOOM
  // Object` resolves its pick through the SAME pipeline a click does — locked /
  // off / frozen gating, the INSERT wrapper lift, child-distance refinement —
  // instead of the bbox-only `SpatialIndex.pick`. `null` until the click
  // handlers install (and again after they tear down): the zoom-object branch
  // documents that fallback.
  const refinedEntityPickRef = useRef<RefinedEntityPicker | null>(null);
  const zoomPickingRef = useRef<ZoomPickingState | null>(null);
  const invalidateRef = useRef<(() => void) | null>(null);
  // MC4-interactive Slice 3 — live viewport-redirect context for the wheel/pan
  // handlers. Refreshed each render (below, after activeLayoutViewports +
  // paperPageSetup are computed) so the handlers read fresh space-context + VP
  // state without re-installing listeners. null until layout/VP context exists.
  const viewportRedirectRef = useRef<ViewportRedirectContext | null>(null);
  // B7 — OSNAP resolver bindings. Hook subscribes to OSMODE / APERTURE /
  // AUTOSNAP sysvars and refreshes the resolver ref synchronously.
  const { osnapBindingsRef, aperture, autosnap, effectiveOsmode } =
    useOsnapBindings({
      spatialIndexRefForHover,
    });
  // OSNAP-TEMPOVERRIDE-WIRE — carrier for the live stage-interactions handle's
  // `refreshSnapAtLastPointer`. Stored in a ref (not state) so the
  // mode-change effect below can call it without re-installing listeners. The
  // binder effect refreshes it on (re)install and nulls it on teardown.
  const refreshSnapAtLastPointerRef = useRef<(() => void) | null>(null);
  // PR-H — BEDIT detection.
  const beditActive =
    (props.activeSpace?.kind as unknown as string) === "block-edit";
  // PR-V0b — install pointer / wheel / keyboard listeners.
  const activeSpaceId = props.activeSpace?.id ?? "model";
  // MC4 iter-2 P1 #165 — In MSPACE_VIA_VP mode the THREE camera has been
  // tweened to model-space view (viewCenter + vpZoom), so unproject already
  // yields model-space world coords.  Route the emitted spaceId to "model"
  // so handleStagePoint's guard (draftSession.spaceId === spaceId) matches
  // the session started via beginDrafting (also overridden to "model").
  // Re-key the effect on effectiveInteractionSpaceId so the handler
  // reinstalls on PSPACE ↔ MSPACE_VIA_VP transitions.
  // (spaceContextMode declared above near hover gate — same render-time value.)
  const effectiveInteractionSpaceId =
    spaceContextMode === "MSPACE_VIA_VP" ? "model" : activeSpaceId;
  const interactionCbsRef = useRef({
    onCanvasPoint: props.onCanvasPoint,
    onCanvasHover: props.onCanvasHover,
    onCursorClientChange: props.onCursorClientChange,
    onRightClickEnter: props.onRightClickEnter,
    onContextMenu: props.onContextMenu,
    onPanComplete: props.onPanComplete,
    onPanSessionEnd: props.onPanSessionEnd,
  });
  interactionCbsRef.current = {
    onCanvasPoint: props.onCanvasPoint,
    onCanvasHover: props.onCanvasHover,
    onCursorClientChange: props.onCursorClientChange,
    onRightClickEnter: props.onRightClickEnter,
    onContextMenu: props.onContextMenu,
    onPanComplete: props.onPanComplete,
    onPanSessionEnd: props.onPanSessionEnd,
  };
  // cmd-pan — interactive realtime-pan session flag. App.tsx owns the ref (it is
  // forwarded as a prop); fall back to a local always-false ref so the stage
  // works standalone (no session = LMB selection unchanged).
  const localPanSessionRef = useRef(false);
  const panSessionActiveRef = props.panSessionActiveRef ?? localPanSessionRef;
  // MC4-interactive Slice 3 — dispatch the geometry/viewport-set-view command
  // for a redirected per-VP zoom/pan. Geometry-via-command-engine: the wheel/
  // pan handlers never mutate the entity directly — they go through this
  // dispatch. The command is in the renderTopologyVersion VP-property-only
  // allowlist, so continuous zoom/pan does NOT bump render topology (smooth).
  const onViewportSetView = useCallback(
    (
      viewportId: string,
      view: { viewCenter?: { x: number; y: number }; viewHeight?: number },
    ) => {
      useEditorStore.getState().dispatch({
        type: "geometry/viewport-set-view",
        source: "scene",
        viewportId,
        ...(view.viewCenter !== undefined ? { viewCenter: view.viewCenter } : {}),
        ...(view.viewHeight !== undefined ? { viewHeight: view.viewHeight } : {}),
      });
    },
    [],
  );
  useEffect(() => {
    const shell = overlayShellRef.current;
    if (!shell) return;
    const handle = bindStageInteractions({
      overlayShell: shell,
      liveCameraRef,
      viewportSizeRef,
      invalidateRef,
      setOverlayState,
      interactionCbsRef,
      activeSpaceId: effectiveInteractionSpaceId,
      // MC4 S3 — un-collapsed active-space id for the snap/hover resolver's
      // identity fallback. In MSPACE_VIA_VP this is the layout id (`activeSpaceId`,
      // line ~968) — NOT the collapsed `effectiveInteractionSpaceId` (= "model")
      // — so that over the paper sheet outside the active VP the resolver scopes
      // the candidate filter to the layout's own geometry (title block, VP
      // border) instead of admitting invisible model geometry. The dispatch/emit
      // space stays `activeSpaceId: effectiveInteractionSpaceId` above.
      identityFallbackSpaceId: activeSpaceId,
      spatialIndexRef: spatialIndexRefForHover,
      // Codex PR #784 round 7 P2 — ZOOM Object picks through the same refined
      // picker the click path uses. Stable ref identity (the CONTENT is
      // republished by the click-handler install), so NOT a dep of this effect.
      refinedEntityPickRef,
      // Codex PR #784 round 4 F2 — hover/click parity for INSERT child
      // visibility (see the ref's declaration above). Stable ref identity, read
      // at event time → NOT a dependency of this effect.
      hiddenLayersRef,
      zoomPickingRef,
      osnapBindingsRef,
      stageCameraApiRefProp: props.stageCameraApiRef,
      // MC4-interactive Slice 3 — per-VP zoom/pan redirect.
      viewportRedirectRef,
      onViewportSetView,
      // MC4 edge case #2 — draft routing past the active VP edge. Stable ref
      // (identity never changes); read at event-time, so NOT a dep below.
      draftActiveRef,
      // OTRACK-SUBSYSTEM — "active point prompt" gate; stable ref.
      draftPointPromptActiveRef,
      // OTRACK-SUBSYSTEM round 6 P2 — draft generation counter; stable ref.
      draftGenerationRef,
      // ORTHO-constraint (AutoCAD ORTHOMODE / F8) — draft anchor for the 0/90°
      // lock. Stable ref (identity never changes), read at event-time → NOT a dep.
      orthoBasePointRef,
      // N6 — POLAR relative-basis reference angle. Stable ref, same rationale.
      polarReferenceAngleRef,
      // cmd-pan — interactive realtime-pan session. The flag ref is stable
      // (App.tsx-owned or local fallback); the end callback reads the latest
      // prop via interactionCbsRef so it never re-installs the listeners.
      panSessionActiveRef,
      onPanSessionEnd: () => interactionCbsRef.current.onPanSessionEnd?.(),
    });
    if (!handle) return;
    // OSNAP-TEMPOVERRIDE-WIRE — publish the refresh handle so the effective-
    // OSNAP effect below can re-resolve the snap at the current cursor when the
    // modes change with no pointer move.
    refreshSnapAtLastPointerRef.current = handle.refreshSnapAtLastPointer;
    return () => {
      refreshSnapAtLastPointerRef.current = null;
      handle.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props.stageCameraApiRef + draftActiveRef + orthoBasePointRef + polarReferenceAngleRef are read at event-time
  }, [effectiveInteractionSpaceId, activeSpaceId, onViewportSetView]);

  // OSNAP-TEMPOVERRIDE-WIRE (@codex P2, PR #346) — when the EFFECTIVE OSNAP
  // bitmask changes (a held Shift temp-override toggled — Shift+D disable /
  // Shift+<letter> force a mode — OR a DSETTINGS/OSMODE dialog edit) WITHOUT a
  // pointer move, re-run the real snap resolution at the LAST cursor position.
  // Otherwise `lastSnappedWorld` + the overlay marker stay glued to the target
  // resolved under the OLD modes, so a click with no intervening move commits
  // the stale snap. `refreshSnapAtLastPointer` no-ops when the cursor is off
  // the canvas (`lastMoveClient` null), so an off-canvas mode change — and the
  // very first mount (no move yet) — does nothing. The synthetic re-resolve
  // does NOT change `effectiveOsmode`, so this effect cannot re-fire itself
  // (no loop). Keyed ONLY on `effectiveOsmode`: aperture/autosnap changes still
  // take effect on the next real move (AutoCAD parity — they don't re-magnet a
  // held cursor), and the ref carrier is intentionally not a dep (a re-install
  // already re-resolves on the next move).
  useEffect(() => {
    refreshSnapAtLastPointerRef.current?.();
  }, [effectiveOsmode]);

  // W2.5 OSNAP-OVERRIDE-MODES-S2 (local Codex round-7 P2) — the SAME
  // stale-cache class applies to `osnapExtendedOverride` (arm / capture /
  // one-shot clear), but this effect did NOT watch it: only `effectiveOsmode`
  // above did. Two concrete gaps that surfaced through code review:
  //   - Escape cancels a captured-but-uncommitted override (`useKeyboardShortcuts
  //     .ts` / the by-construction draft-session-lifecycle clear in App.tsx)
  //     entirely OUTSIDE the pick loop's own closure — neither seam can reach
  //     `lastSnappedWorld` directly. Without this effect, a click with no
  //     intervening move after the Escape-cancel would still commit the
  //     CANCELLED extended crossing (still cached from before the cancel).
  //   - Symmetrically, arming a NEW override via the Shift+right-click menu
  //     while the cursor is already resting on a valid entity (no move needed
  //     to re-trigger `resolveAndCacheSnap`) leaves the marker/tooltip glued
  //     to whatever the PRE-arm running-snap state was.
  // Re-resolving on ANY `osnapExtendedOverride` change covers arm / capture /
  // commit-clear / cancel-clear uniformly — the same one-effect breadth
  // `effectiveOsmode` already gives the temp-override class, rather than each
  // individual mutation site trying to reach into the pick loop's private
  // cache itself (which `tryCaptureExtendedOverrideOperand` already does for
  // its OWN synchronous capture-then-refresh step — this effect covers every
  // OTHER, asynchronous-relative-to-the-pick-loop mutation path).
  const osnapExtendedOverride = useSysvarStore((s) => s.osnapExtendedOverride);
  useEffect(() => {
    refreshSnapAtLastPointerRef.current?.();
  }, [osnapExtendedOverride]);

  // ORTHO-STALE-CACHE (@codex P2) — the SAME re-resolve must run when the
  // EFFECTIVE ORTHO state flips (F8 / ORTHOMODE toggle OR a held-Shift temp
  // override) WITHOUT a pointer move. `resolveAndCacheSnap` recomputes
  // `orthoWorld` → `lastSnappedWorld` from the live effective-ortho read, so a
  // stale cache is the failure otherwise: move with ortho OFF (cache = diagonal),
  // press F8, then click without moving → the old diagonal commits instead of the
  // 0/90° point; the inverse leaves a stale locked point after ortho off.
  //
  // @codex P2 (round 3) — the cache is ALSO stale when the ANCHOR itself appears
  // or moves with no pointer move: the resolver reads `orthoBasePointRef.current`
  // (refreshed each render, above), so if the first LINE point / base is typed as
  // a coordinate while the cursor already rests on the canvas, the next click
  // without moving would otherwise re-use the pre-anchor RAW cached cursor and
  // commit a diagonal point. Re-key on `orthoAnchorKey` so the re-resolve fires
  // on anchor change too. Keyed on primitives (boolean + string|null) so each
  // fires once per genuine change (Object.is → no churn); the re-resolve mutates
  // neither ortho nor the anchor, so no loop — mirroring the `effectiveOsmode`
  // refresh above. (`refreshSnapAtLastPointer` no-ops off-canvas / pre-first-move.)
  //
  // N6 — `polarReferenceAngleKey` re-keys the SAME re-resolve: a new point
  // advances both the ORTHO anchor AND the polar reference angle in
  // lockstep, and a click with no intervening move must not commit against
  // the STALE reference angle's rotated table.
  const effectiveOrtho = useSysvarStore(selectEffectiveOrtho);
  useEffect(() => {
    refreshSnapAtLastPointerRef.current?.();
  }, [effectiveOrtho, orthoAnchorKey, polarReferenceAngleKey]);

  // POLAR aid effects (#492 @codex P2): refresh snap on polar-config change + clear guide on draft end.
  usePolarDraftAidEffects({ refreshSnapAtLastPointerRef, setOverlayState, isDrafting });

  // OTRACK-SUBSYSTEM: Shift-tracker mount + acquired-point lifecycle clears
  // + stale-cache refresh (mirrors the POLAR effect immediately above).
  useOtrackAcquisitionEffects({
    isDrafting,
    draftCommand: props.draftSession?.command ?? null, // round 6/7 P2 — see the hook's own doc.
    draftPointCount: props.draftSession?.points.length ?? 0, // round 10 P2 — see the hook's own doc.
    refreshSnapAtLastPointerRef,
    setOverlayState,
  });

  // PR-V0b — populate `stageCameraApiRef`.
  const stageCameraApiRefProp = props.stageCameraApiRef;
  const onZoomPromptChange = props.onZoomPromptChange;
  // #284-6 — completed-interactive-zoom view-op telemetry hook. Read at
  // event-time via a stable ref so its identity never re-runs the mount effect
  // (which would tear down + rebuild the camera API on every render).
  const onZoomViewOpCompleteRef = useRef(props.onZoomViewOpComplete);
  onZoomViewOpCompleteRef.current = props.onZoomViewOpComplete;
  const selectedEntityIds = props.selectedEntityIds;
  const entitiesForBoundsRef = useRef<typeof props.entities>(props.entities);
  entitiesForBoundsRef.current = props.entities;
  const selectedIdsRef = useRef<readonly string[]>(selectedEntityIds);
  selectedIdsRef.current = selectedEntityIds;
  useEffect(() => {
    return mountStageCameraApi({
      stageCameraApiRefProp,
      cameraStateRef,
      viewportSizeRef,
      spatialIndexRefForHover,
      pendingTweenRef,
      zoomPickingRef,
      selectedIdsRef,
      onZoomPromptChange,
      onZoomViewOpComplete: (mode) => onZoomViewOpCompleteRef.current?.(mode),
    });
  }, [stageCameraApiRefProp, onZoomPromptChange]);
  // PR-V0c / GRIPS-INTERACTION PR-1/2 — grip drag start handler + click-arm ↔
  // hotGripStore bridge (arm/disarm/mode-cycle/commit echo). Extracted to
  // `useGripStartBundle` (file-size split, RP4 pattern — see the hook).
  const handleGripStart = useGripStartBundle({
    liveCameraRef,
    overlayShellRef,
    osnapBindingsRef,
    spatialIndexRef: spatialIndexRefForHover,
    activeSpaceIdRef: activeSpaceIdForHoverRef,
    selectedIdsRef,
    onGripDrag: props.onGripDrag,
    onGripDragStart: props.onGripDragStart,
    onGripDragEnd: props.onGripDragEnd,
    onGripDragCancel: props.onGripDragCancel,
    onGripTransformCommit: props.onGripTransformCommit,
    onGripModeEcho: props.onGripModeEcho,
  });

  // Update BEDIT prop when the active space toggles.
  useEffect(() => {
    setOverlayState(buildBeditOverlayPatch(beditActive));
  }, [beditActive]);

  // B11 — PrintPreview bounds driver. Re-projects world bounds to
  // client pixels whenever the prop changes.
  //
  // Phase 1.k (П35Р React #185 fix) — `useCameraSnapshotVersion` was a
  // hard subscriber to a version counter bumped from inside useFrame on
  // every R3F invalidate. During the 685k bulk-load the scene
  // invalidates ~hundreds of times in tight bursts, racing React's
  // commit phase: by the time useSyncExternalStore's mount effect
  // re-reads `getCameraVersion()` the value already bumped → React
  // schedules forceStoreRerender, the next commit re-reads, version
  // bumped again, ad infinitum until the 50-nested-update budget trips
  // error #185.
  //
  // Codex P2 follow-up: keep the camera-tracking when the dialog IS
  // open (otherwise the preview rect freezes on pan/zoom). We split
  // the subscription into a child component `<PrintPreviewCameraBinder>`
  // that only mounts when `printPreviewWorldBounds !== null`. Once
  // bulk-load has settled the user is free to open the dialog; by
  // then there is no scenario where the binder accumulates 50 forced
  // re-renders in a single commit cycle.
  const printPreviewWorldBounds = props.printPreviewWorldBounds ?? null;
  // PDF-6.7 fix: while the user is actively picking the Window on the canvas,
  // the pick controller below OWNS the overlay (it paints the live rubber-band
  // rect). Suppress BOTH this memo-driven repaint AND the camera binder (mount
  // gated on `!windowPickActive` below) so the old/persisted bounds can't fight
  // the live rect — otherwise the rectangle "keeps reverting to the old frame"
  // on every camera-version bump during the drag.
  const windowPickActive = props.windowPickActive === true;
  useEffect(() => {
    if (windowPickActive) return;
    applyPrintPreview(setOverlayState, printPreviewWorldBounds);
  }, [printPreviewWorldBounds, windowPickActive]);

  // PDF-6.7 — interactive plot Window-pick. When `windowPickActive`, install
  // a dedicated lightweight 2-corner pick controller on the stage shell
  // (`scene/windowPickController.ts`). It reuses the same `clientToWorld`
  // unprojection the wheel/drag/OSNAP handlers use, so for the ACTIVE paper
  // layout the picked corners are paper-mm world coords. The live rectangle
  // drives the SAME print-preview overlay (`applyPrintPreview`) so the user
  // sees the translucent rect grow as they drag; two corners commit via
  // `onWindowPicked`, Esc / right-click aborts via `onWindowPickCancel`.
  //
  // Callbacks are read through a ref so the controller installs once per
  // activation without re-binding listeners when the parent re-renders.
  const windowPickCbsRef = useRef({
    onWindowPicked: props.onWindowPicked,
    onWindowPickCancel: props.onWindowPickCancel,
  });
  windowPickCbsRef.current = {
    onWindowPicked: props.onWindowPicked,
    onWindowPickCancel: props.onWindowPickCancel,
  };
  useEffect(() => {
    if (!windowPickActive) return;
    const shell = overlayShellRef.current;
    if (!shell) return;
    return installWindowPickController({
      overlayShell: shell,
      cameraRef: liveCameraRef,
      onPreview: (rect) => {
        // Live rubber-band: paint the in-progress rect via the SAME print-
        // preview overlay the dialog uses. `null` (before corner 1 / on
        // teardown) clears it — the dedicated `printPreviewWorldBounds`
        // effect repaints the correct rect once the dialog returns.
        applyPrintPreview(setOverlayState, rect ?? null);
      },
      onPicked: (rect) => {
        windowPickCbsRef.current.onWindowPicked?.(rect);
      },
      onCancel: () => {
        windowPickCbsRef.current.onWindowPickCancel?.();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks read via ref so the controller installs once per activation
  }, [windowPickActive]);

  // B16 — wire CURSORSIZE / CURSORCOLOR / APERTURE / AUTOSNAP-aperture
  // bit from the sysvar store into the overlay state. PICKBOX feeds
  // the AutoCAD-style pickbox sprite at the cursor.
  const cursorSize = useSysvarStore((s) => s.values.CURSORSIZE);
  const cursorColor = useSysvarStore((s) => s.values.CURSORCOLOR);
  const pickbox = useSysvarStore((s) => s.values.PICKBOX);
  useEffect(() => {
    setOverlayState(
      buildCursorSysvarOverlayPatch({
        cursorSize,
        cursorColor,
        autosnap,
        aperture,
        pickbox,
      }),
    );
  }, [cursorSize, cursorColor, autosnap, aperture, pickbox]);

  // MC2 — PaperSpaceLayer: resolve page setup for the active layout space.
  // `layoutsBySpaceId` is keyed by the disambiguated spaceId (e.g.
  // "layout:Beta (1)") so duplicate-name drawings resolve correctly.
  // Codex P1 #162: the old name-strip lookup collapsed when two tabs shared
  // the same raw DXF name — importLayouts keyed by raw name so the second
  // entry overwrote the first; "layout:Beta (1)".slice("layout:".length)
  // produced "Beta (1)" which was never in the map.
  const layoutsBySpaceId = useEditorStore(
    (s) => s.geometryDocument.layoutsBySpaceId,
  );
  // Legacy fallback: if layoutsBySpaceId is not yet populated (e.g. older
  // merge paths that haven't been rebuilt), fall back to name-based lookup
  // in `layouts` dict. This keeps existing flows working until all merge
  // paths populate layoutsBySpaceId (Codex P1 #162).
  const layoutsByName = useEditorStore(
    (s) => s.geometryDocument.layouts,
  );
  const activeSpaceKind = props.activeSpace?.kind ?? "model";
  const activeSpaceName = props.activeSpace?.name ?? "";
  // `activeSpaceId` is declared above (line ~308) as props.activeSpace?.id ?? "model".
  const activeLayout =
    activeSpaceKind === "layout"
      ? (layoutsBySpaceId[activeSpaceId] ?? layoutsByName[activeSpaceName] ?? null)
      : null;

  // MC3 — viewport entities for the active layout space.
  // Viewport entities are retained in space.entities[] even when streaming
  // discards other entities (MC3 fix-6), so this is always authoritative.
  // Synthetic spaces (BEDIT / block-edit) project to kind="model" → guard.
  const activeSpaceRawEntities = useEditorStore(
    (s) =>
      activeSpaceKind === "layout"
        ? (s.geometryDocument.spaces.find((sp) => sp.id === activeSpaceId)
            ?.entities ?? null)
        : null,
  );
  // Render topology version — incremented on entity mutations but NOT on
  // VP-property-only commands (lock/scale/vplayer/clip). Keyed by Scene3DStage
  // render caches to avoid O(N) spatial rebuilds on every VP lock toggle.
  const geometryVersion = useEditorStore(
    (s) => s.renderTopologyVersion,
  );
  // gap2-iter7: layer table for BYLAYER VP frame color resolution.
  // Subscribed to styleTables.layers array; memo-stabilized into a Map keyed
  // by lower-cased layer name so ViewportFrameLayer can do O(1) lookups.
  const rawLayers = useEditorStore(
    (s) => s.geometryDocument.styleTables?.layers ?? EMPTY_LAYER_DEFS,
  );
  const vpFrameLayerMap = useMemo<ReadonlyMap<string, LayerDefinition>>(() => {
    const m = new Map<string, LayerDefinition>();
    for (const l of rawLayers) m.set(l.name.toLowerCase(), l);
    return m;
  }, [rawLayers]);
  const activeLayoutViewports = useMemo(
    () => {
      if (activeSpaceKind !== "layout") return [];
      // MC3 fix-6 retains Viewport entities in space.entities[] even during
      // streaming-discard, so space.entities is authoritative for all cases.
      // Fix-1: no fallback to stale viewportsBySpaceId — deleted VPs must NOT
      // be revived when the live entity list is empty.
      // Fix-2/iter-7/iter-8: control-VP identification (explicit group-69
      // viewportId=1 ONLY — @codex round-11, see `viewportControlFilter.ts`'s
      // doc comment for why an area-based fallback was tried and removed)
      // is the shared `excludeControlViewport` helper — VPCLIP-INTERACTIVE's
      // `findViewportAtPaperPoint` hit-test consumes the SAME rule (one
      // truth, CLAUDE.md PRIORITY #2).
      const fromSpace = activeSpaceRawEntities?.filter(
        (e): e is ViewportEntity =>
          e.type === "Viewport" && e.viewportStatus > 0,
      ) ?? [];
      return excludeControlViewport(fromSpace);
    },
    [activeSpaceRawEntities, activeSpaceKind],
  );
  const paperPageSetup = useMemo(() => {
    // Codex P2 #162 iter-5: synthetic layouts (ENTITIES-only DXFs) have no
    // Layout object → fall back to defaultPageSetup so paper sheet + camera
    // fit still render instead of silently disappearing.
    if (activeLayout === null)
      return activeSpaceKind === "layout" ? defaultPageSetup() : null;
    const setup = extractPageSetup(activeLayout);
    if (!setup) return defaultPageSetup();
    // Pixel-unit layouts cannot be placed in world space — fall back to
    // default A4 sheet with a warning so the paper outline stays visible.
    if (setup.units === "pixels") {
      console.warn(
        `[PaperSpace] Layout "${activeSpaceId}" uses pixel plot units (plotPaperUnits=2); ` +
          "falling back to default A4 page setup.",
      );
      return defaultPageSetup();
    }
    return setup;
  }, [activeLayout, activeSpaceId, activeSpaceKind]);

  // The ONE derivation of the active layout's sheet origin in the renderer's
  // OFFSET-world frame. `paperPageSetup.origin` is a DOCUMENT coordinate; the
  // sheet mesh, the layout auto-fit camera, the VP frame meshes, the VP
  // scissor rects, the paper↔model transform and every paper-space pointer
  // hit-test must all be placed from THIS value, because every geometry
  // adapter already subtracts the locked session origin from paper-space
  // entities too. Threading one derived value (rather than converting at each
  // site) is what keeps the seven consumers below from drifting apart
  // (CLAUDE.md PRIORITY #2.1).
  //
  // `null` exactly when `paperPageSetup` is null (no layout active), so every
  // consumer's existing `paperPageSetup !== null` guard still covers it.
  //
  // DRAFT-DOCUMENT-FRAME — the DRAFT STATE MACHINE is the one consumer that
  // must NOT read this value. It works in DOCUMENT coordinates (the pick loop
  // converts at the `onCanvasPoint` / `onCanvasHover` emits via
  // `ActiveInputSpace.toDocument`), so it resolves the same sheet origin
  // through its own document-frame derivation,
  // `hooks/useDraftSession.resolveDraftPaperOriginDocument`. Two values, one
  // per frame; the names say which, and each derivation site names the other.
  const lockedCoordinateOrigin = getCoordinateOrigin();
  const paperOriginOffset = useMemo(() => {
    // `applyOffsetToPaperOrigin` reads the MODULE-level locked origin, which
    // `react-hooks/exhaustive-deps` cannot see. `lockedCoordinateOrigin` is
    // that same value hoisted into the render so it can be a real dependency:
    // the lock is keep-first and returns a stable object reference once set,
    // so this costs one extra comparison and guarantees the memo cannot cache
    // a pre-lock (identity) conversion. The reference below is what makes the
    // dependency legible to the linter as well as to a reader.
    void lockedCoordinateOrigin;
    if (paperPageSetup === null) return null;
    return applyOffsetToPaperOrigin(paperPageSetup.origin);
  }, [paperPageSetup, lockedCoordinateOrigin]);

  // Camera auto-fit when switching to a layout tab — fit the paper sheet
  // so the user sees the full A4/A3/… page without having to ZOOM EXTENTS.
  const prevActiveSpaceIdRef = useRef(props.activeSpace?.id ?? "model");
  useEffect(() => {
    const nextId = props.activeSpace?.id ?? "model";
    const switched = nextId !== prevActiveSpaceIdRef.current;
    prevActiveSpaceIdRef.current = nextId;
    if (!switched || activeSpaceKind !== "layout" || !paperPageSetup) return;
    // `computeLayoutFitCamera` centres on the OFFSET-world sheet centre (it
    // converts `pageSetup.origin` itself, so this site cannot pass the wrong
    // frame). Centring on the RAW sheet centre is the defect that rendered
    // every layout of a non-zero-origin drawing as a blank white page.
    const target: CameraStateSnapshot | null = computeLayoutFitCamera(
      paperPageSetup,
      viewportSizeRef.current,
    );
    if (!target) return;
    // Drive via tween path so CameraApiBinder applies to the live
    // OrthographicCamera; writing cameraStateRef directly has no effect
    // because CameraApiBinder overwrites it from ortho every frame.
    pendingTweenRef.current = {
      target,
      origin: { ...cameraStateRef.current },
      startedAt:
        typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now(),
      durationMs: CAMERA_TWEEN_DURATION_MS,
    };
    invalidateRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.activeSpace?.id]);

  // MC4 — MSPACE_VIA_VP: install dblclick + Esc controller when a layout with
  // viewports is active.  Re-installs when the VP list or paper origin changes.
  const activeViewportId = useSpaceContextStore((s) => s.activeViewportId);

  // MC4-interactive Slice 3 — refresh the viewport-redirect context every
  // render so the (once-installed) wheel/pan handlers read fresh state. The
  // getter resolves the active VP from the live activeLayoutViewports list and
  // computes its effective lock (entity field OR XDATA override). null paper
  // origin (non-layout) leaves the context disabled (mode guard also covers).
  viewportRedirectRef.current = paperOriginOffset
    ? {
        mode: spaceContextMode,
        activeViewportId,
        paperOrigin: paperOriginOffset,
        // The entity → `ViewportLike` projection (incl. the DXF 17/27
        // `viewTarget` half of the window anchor and the effective lock) lives
        // in `viewports/redirectViewportView.ts` so it is unit-testable on its
        // own — it is a synthetic literal, which is exactly the shape a newly
        // required field gets silently dropped from.
        getActiveViewport: () =>
          resolveRedirectViewportView(activeLayoutViewports, activeViewportId),
      }
    : null;

  // MC4 Slice 4 — MODEL→PAPER mapping for the in-VP draft rubber band.
  // Reuses the SAME active-VP source the S2/S3 pointer paths consume (the
  // live `viewportRedirectRef.current` set just above — its `getActiveViewport`
  // + `paperOrigin`); it does NOT re-resolve the VP or recompute the origin.
  // Non-null ONLY when drafting inside an unlocked active viewport in
  // MSPACE_VIA_VP with a model-space preview entity; otherwise null →
  // `renderableToDraftUpdate` runs its byte-identical no-transform path.
  //
  // Keyed on the scalars that gate + define the transform: the redirect
  // context identity (refreshed each render, so a VP pan/zoom that bumps
  // `geometryVersion` re-runs this), the layout origin, the space-context
  // mode, the active VP id, the live VP view params, and the preview space.
  // `activeLayoutViewports` carries the live `viewCenter` / `viewHeight` /
  // `viewTwistAngle`, so the mapping re-derives when the VP view changes
  // mid-draft and the rubber band re-maps.
  const draftPreviewSpaceId = props.draftPreviewEntity?.spaceId ?? null;
  const previewMapping = useMemo(
    () =>
      resolveDraftPreviewMapping(
        viewportRedirectRef.current,
        paperOriginOffset,
        draftPreviewSpaceId,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- viewportRedirectRef is a ref refreshed every render; the listed deps cover every input that changes the resolved mapping (VP view params ride activeLayoutViewports).
    [
      spaceContextMode,
      activeViewportId,
      paperOriginOffset,
      activeLayoutViewports,
      draftPreviewSpaceId,
    ],
  );

  useEffect(() => {
    const shell = overlayShellRef.current;
    if (!shell || activeSpaceKind !== "layout") return;
    if (activeLayoutViewports.length === 0) {
      // MC4 iter-5 P2 #165: VP set became empty while in MSPACE_VIA_VP.
      // The previous controller cleanup already removed event listeners but
      // did NOT exit the FSM — force transition to PSPACE now so the app
      // is not stuck in an orphaned MSPACE_VIA_VP state with no viewports.
      const store = useSpaceContextStore.getState();
      if (store.mode === "MSPACE_VIA_VP") {
        store.exitViewport();
        useEditorStore.getState().dispatch({
          type: "shell/set-layout-context",
          source: "scene",
          layoutContext: "paper",
        });
        const saved = vpControllerSavedRef.current;
        if (saved) {
          const cam = sceneCameraRef.current;
          // MC4 iter-6 P2 #165 fix #2: reset up + lookAt immediately before
          // the return tween so there is no twist during animation (mirrors
          // doExitViewport Fix #2 in the controller).
          if (cam) {
            cam.up.set(0, 1, 0);
            cam.lookAt(cam.position.x, cam.position.y, 0);
            cam.updateMatrixWorld();
          }
          // MC4 iter-7 P1 #1: Forced empty-VP exit must schedule the return
          // tween unconditionally. The previous iter-6 guard
          // (`pendingTweenRef.current === null`) blocked the forced-exit tween
          // when an enter-tween was in-flight, leaving the camera stuck in
          // MSPACE_VIA_VP with no VP to anchor to.
          // Fix: treat any in-flight tween as superseded — cancel it and
          // schedule the forced-exit return tween. The only exception is when
          // the existing tween is ALREADY targeting the saved paper camera
          // (a return tween from a prior Esc); in that case the active tween
          // will self-clean via its own onComplete, so we leave it alone.
          const paperCamera = saved.camera;
          const existingTween = pendingTweenRef.current;
          const alreadyReturning =
            existingTween !== null &&
            existingTween.target.centerX === paperCamera.centerX &&
            existingTween.target.centerY === paperCamera.centerY &&
            existingTween.target.zoom === paperCamera.zoom;
          if (!alreadyReturning) {
            // Cancel any in-flight enter-tween and replace with forced-exit return tween.
            const capturedCam = cam;
            // MC4 iter-8 P2 #4: mark exit-tween active so re-entry during the
            // forced-exit return tween is guarded (mirrors doExitViewport pattern).
            vpExitTweenRef.current = true;
            pendingTweenRef.current = {
              target: paperCamera,
              origin: { ...cameraStateRef.current },
              startedAt:
                typeof performance !== "undefined" && performance.now
                  ? performance.now()
                  : Date.now(),
              durationMs: CAMERA_TWEEN_DURATION_MS,
              // Fix #2: re-apply lookAt at tween completion so the quaternion
              // is exact at the final paper position.
              // Fix #1: defer snapshot clear to onComplete — forced-exit tween
              // supersedes the enter tween; onComplete is the authoritative
              // cleanup point.
              onComplete: () => {
                if (capturedCam) {
                  capturedCam.lookAt(
                    paperCamera.centerX,
                    paperCamera.centerY,
                    0,
                  );
                  capturedCam.updateMatrixWorld();
                }
                vpControllerSavedRef.current = null;
                // MC4 iter-8 P2 #4: clear exit-tween guard on completion.
                vpExitTweenRef.current = false;
              },
            };
            invalidateRef.current?.();
          }
          // If alreadyReturning, the active tween targets the correct paper camera
          // and its onComplete will clear vpControllerSavedRef; do not interfere.
        }
      }
      return;
    }
    if (!paperPageSetup || !paperOriginOffset) return;
    return installViewportContextController({
      overlayShell: shell,
      cameraRef: sceneCameraRef,
      cameraStateRef,
      pendingTweenRef,
      viewportSizeRef,
      invalidateRef,
      viewports: activeLayoutViewports,
      paperOrigin: paperOriginOffset,
      paperSnapshotRef: vpControllerSavedRef,
      // MC4 iter-7 P2 #6: persist exit-tween reentry guard across reinstalls.
      exitTweenRef: vpExitTweenRef,
      // MC4 iter-4 P2 #165: keep editorStore.drawing.layoutContext in sync
      // with the PSPACE ↔ MSPACE_VIA_VP FSM so UI consumers and AI drafting
      // that read layoutContext see the authoritative value.
      onLayoutContextChange: (ctx) => {
        useEditorStore.getState().dispatch({
          type: "shell/set-layout-context",
          source: "scene",
          layoutContext: ctx,
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable; paperOrigin tracked via paperOriginOffset identity
  }, [activeSpaceKind, activeLayoutViewports, paperPageSetup, paperOriginOffset]);

  // GripsDomLayer optional-callback adapters. Returning `undefined`
  // omits the prop entirely so the grip overlay can fall back to its
  // default behaviour (browser-default context menu, no lookup popup).
  const lookupGripClickAdapter = useMemo(
    () => buildLookupGripClickAdapter(props.onBlockLookupGripClick),
    [props.onBlockLookupGripClick],
  );
  const mleaderGripContextMenuAdapter = useMemo(
    () => buildMleaderGripContextMenuAdapter(props.onMleaderGripContextMenu),
    [props.onMleaderGripContextMenu],
  );

  return (
    <div
      ref={overlayShellRef}
      className="scene3d-stage drawing-stage-card"
      data-testid="drawing-stage"
      data-testid-legacy="drawing-stage-card"
      data-renderer="webgl"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 600,
      }}
    >
      {/*
        Sentinel sibling carrying the legacy `drawing-stage-card` testid.
        Pointer-events disabled so it never intercepts clicks intended
        for the canvas.
      */}
      <div
        data-testid="drawing-stage-card"
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      />
      {printPreviewWorldBounds !== null && !windowPickActive ? (
        <PrintPreviewCameraBinder
          bounds={printPreviewWorldBounds}
          setOverlayState={setOverlayState}
        />
      ) : null}
      <Canvas
        frameloop="demand"
        dpr={[1, 2]}
        // `stencil: true` is REQUIRED for the non-rectangular viewport clip
        // (XCLIP / VPCLIP / SPATIAL_FILTER) render arm: `renderViewportPass` →
        // `beginViewportClipStencil` marks the clip polygon into the WebGL
        // stencil buffer and EQUAL-tests every model material against it. three
        // r163+ defaults the WebGLRenderer `stencil` context attribute to FALSE
        // (perf), so without this the drawing buffer carries NO stencil bits →
        // every stencil write/test is a silent no-op → the pentagon clip never
        // cuts geometry and the VP renders unclipped. (`preserveDrawingBuffer`
        // keeps the composited frame valid for the visual gate's readPixels /
        // screenshot — CLAUDE.md PRIORITY #3.)
        //
        // `antialias: true` is made EXPLICIT (rather than relying on the R3F /
        // three default) as fat-line MSAA hardening: the batched `LineMaterial`
        // fat lines (LineSegments2) anti-alias their edges via backbuffer MSAA,
        // and some drivers drop MSAA on the default framebuffer when
        // `preserveDrawingBuffer: true` is also requested. Stating the flag
        // explicitly documents the requirement and guards against a future R3F
        // default change silently disabling it. Verified live: the capture env
        // reports `gl.getParameter(SAMPLES) === 4` with all three flags set
        // (docs/qa/line-continuity-render-notes.md). NOTE: this does NOT fix the
        // separate longitudinal line-beading artifact under investigation there
        // (that reproduces WITH SAMPLES=4 and is not MSAA-drop / not
        // alphaToCoverage — root cause still open).
        gl={{ preserveDrawingBuffer: true, stencil: true, antialias: true }}
        // MC4 clip slice — enable per-material local clipping on the renderer so
        // the draft-preview material's `clippingPlanes` (set by DraftPreviewMesh
        // / DraftPreviewLayer) clip the in-VP rubber band to the active VP rect.
        // Set in `onCreated` (guaranteed to run on the live WebGLRenderer
        // instance) rather than via the `gl` prop object — `localClippingEnabled`
        // is not a WebGLRenderer constructor parameter, so the explicit instance
        // write is the unambiguous path. `preserveDrawingBuffer` stays in `gl`
        // (it IS a constructor param) so the visual gate's readPixels/screenshot
        // still see the composited frame. The flag is global, but only materials
        // that carry `clippingPlanes` are clipped — and ONLY the 4 draft-preview
        // materials ever get planes — so no other scene material is affected.
        onCreated={({ gl }) => {
          gl.localClippingEnabled = true;
          sceneCanvasRef.current = gl.domElement; // test-bridge projection
          // MC4 clip slice — read-only E2E observability mirror (no production
          // code reads it), same legitimate pattern as `__edit_dwg_draftpreview`
          // / `__edit_dwg_draftclip` / `__cam`. Exposes the live renderer's
          // `localClippingEnabled` so a headless gate can confirm the clip is
          // actually armed on the WebGLRenderer instance (the pixel sample is
          // the primary proof; this makes the structural state explicit too).
          if (typeof window !== "undefined") {
            (
              window as unknown as { __edit_dwg_localClippingEnabled?: boolean }
            ).__edit_dwg_localClippingEnabled = gl.localClippingEnabled;
          }
        }}
        style={{
          width: "100%",
          height: "100%",
          // The WebGL buffer is cleared TRANSPARENT, so this CSS colour IS the
          // drawing-area background the user sees — and the background is what
          // AutoCAD colour 7 contrasts against. It must therefore come from the
          // SAME one truth that decides the foreground
          // (`rendering/colorResolution.ts`): dark in model space, white paper
          // in a layout. Hard-coding the dark value here while a layout paints
          // a white sheet is what made on-sheet colour-7 geometry invisible.
          background: backgroundHexForSpaceKind(activeSpaceKind),
        }}
      >
        <OrthographicCamera
          makeDefault
          manual
          near={-1000}
          far={1000}
        />
        <InitialCameraMount cameraRef={sceneCameraRef} />
        {/* MC2 — paper sheet rendered behind model entities when in layout tab */}
        {paperPageSetup !== null && paperOriginOffset !== null && (
          <PaperSpaceLayer
            pageSetup={paperPageSetup}
            origin={paperOriginOffset}
          />
        )}
        {/* MC3 — viewport frame borders, lock badges, and resize grips overlay */}
        {/* MC4 — activeViewportId prop highlights the active VP in MSPACE_VIA_VP */}
        {/* MC5 — onSelectViewport enables single-click VP selection → ViewportInspector */}
        {paperPageSetup !== null && paperOriginOffset !== null && activeLayoutViewports.length > 0 && (
          <ViewportFrameLayer
            viewports={activeLayoutViewports}
            paperOrigin={paperOriginOffset}
            activeViewportId={activeViewportId}
            layerMap={vpFrameLayerMap}
            onSelectViewport={(vpId) => {
              useEditorStore.getState().dispatch({
                type: "shell/select-entities",
                source: "scene",
                entityIds: [vpId],
              });
            }}
          />
        )}
        {/* MC3 P1 #1 — per-viewport scissored model-geometry render passes.
            Mounts only in layout mode with active VPs; unmounting restores
            R3F auto-render for model space.
            MC4 Slice 2: orchestrator now renders in ALL paper-space modes
            (PSPACE and MSPACE_VIA_VP). AutoCAD parity — entering a viewport
            keeps the layout sheet + every VP's model render on screen; the
            active VP is edited in-place inside its rectangle, NOT expanded
            full-canvas. The active VP is highlighted via ViewportFrameLayer's
            activeViewportId prop (above). Supersedes the full-canvas
            MspaceViaVpDirectPass that previously ran in MSPACE_VIA_VP (dead
            since MC4 Slice 2; deleted in Codex PR #784 round 2).

            MC4 S3 (model/paper-space separation fix): mount on ANY layout that
            has a paper page setup, EVEN with zero viewports. The orchestrator's
            useFrame is what pre-hides the model batched layer (step 1) and
            re-shows model ONLY inside each VP's scissor rect (step 4). With 0
            VPs the per-VP loop is a no-op, but the model-hide + paper-sheet
            render still run, so the screen shows a blank sheet (plus any
            paper-space objects). Previously the `activeLayoutViewports.length > 0`
            gate left an empty layout without the orchestrator → the model
            batched layer was never hidden → model geometry leaked directly onto
            the paper sheet (AutoCAD parity violation: a layout shows model only
            through viewports, never directly). The empty-VP path in the
            orchestrator's useFrame is safe — step 4 early-returns on
            `viewports.length === 0` after the model-hide + paper render. */}
        {paperPageSetup !== null && paperOriginOffset !== null && activeSpaceKind === "layout" && (
          <ViewportRenderOrchestrator
            viewports={activeLayoutViewports}
            paperOrigin={paperOriginOffset}
            spatialIndexRef={spatialIndexRefForHover}
            renderSatellitesRef={renderSatellitesRef}
            renderSatellitesVersionRef={renderSatellitesVersionRef}
            spaceLayerCensusRef={spaceLayerCensusRef}
            viewportLiftRequestsRef={viewportLiftRequestsRef}
            geometryVersion={geometryVersion}
            sheetSizeWld={{ w: paperPageSetup.sheetSize.widthMm, h: paperPageSetup.sheetSize.heightMm }}
          />
        )}
        <SceneContent
          entities={props.entities}
          modelSpaceEntities={props.modelSpaceEntities}
          activeSpaceKind={props.activeSpace?.kind ?? "model"}
          // MC4 S2 P2 (iter-4 #1) — identity-mode fallback space for in-canvas
          // pick + drag-window select (authoritative even for an empty layout).
          activeSpaceId={activeSpaceId}
          handleRef={handleRef}
          batchedLayerRef={batchedLayerRef}
          onSelectEntity={props.onSelectEntity}
          onSelectEntities={props.onSelectEntities}
          selectedEntityIds={props.selectedEntityIds}
          blockDefinitions={props.blockDefinitions}
          cannoscaleName={props.cannoscaleName}
          annotationScales={props.annotationScales}
          annoAllVisible={props.annoAllVisible}
          linetypes={props.linetypes}
          fillmode={props.fillmode}
          linetypeSysvars={props.linetypeSysvars}
          textStyles={props.textStyles}
          spatialIndexRefForHover={spatialIndexRefForHover}
          liveCameraRef={liveCameraRef}
          sharedLockedLayersRef={lockedLayersRef}
          sharedHiddenLayersRef={hiddenLayersRef}
          // Codex PR #784 round 7 P2 — outlet the in-canvas click installer
          // publishes its refined picker into; `bindStageInteractions` above
          // reads the SAME ref for ZOOM Object.
          sharedRefinedEntityPickRef={refinedEntityPickRef}
          sharedRenderSatellitesRef={renderSatellitesRef}
          sharedRenderSatellitesVersionRef={renderSatellitesVersionRef}
          sharedSpaceLayerCensusRef={spaceLayerCensusRef}
          sharedViewportLiftRequestsRef={viewportLiftRequestsRef}
          cameraStateRef={cameraStateRef}
          invalidateRef={invalidateRef}
          // MC4 S2 — same redirect ref handed to installStageInteractions
          // (single source of truth); routes in-VP pick + drag-window select.
          viewportRedirectRef={viewportRedirectRef}
          // cmd-pan — same session flag handed to installStageInteractions;
          // suppresses selection click/drag while a realtime pan is active.
          panSessionActiveRef={panSessionActiveRef}
        />
        <DraftPreviewLayer
          draftPreviewEntity={props.draftPreviewEntity ?? null}
          previewMapping={previewMapping}
        />
        <CameraApiBinder
          cameraStateRef={cameraStateRef}
          viewportSizeRef={viewportSizeRef}
          pendingTweenRef={pendingTweenRef}
        />
        <Scene3DWorldOverlays state={overlayState} />
        <GripsCameraTracker />
      </Canvas>
      <GripsDomLayer
        entities={props.entities}
        selectedEntityIds={props.selectedEntityIds}
        blockDefinitions={props.blockDefinitions}
        suppressed={isDrafting}
        lockedLayers={lockedLayersRef.current}
        resolveTypedEntity={resolveTypedEntityById}
        resolveRenderable={resolveGripRenderable}
        onGripStart={handleGripStart}
        {...(lookupGripClickAdapter
          ? { onLookupGripClick: lookupGripClickAdapter }
          : {})}
        {...(mleaderGripContextMenuAdapter
          ? { onGripContextMenu: mleaderGripContextMenuAdapter }
          : {})}
      />
      {/* Canvas-relative DOM overlay slot — see `Scene3DStageProps`. */}
      {props.stageShellOverlay}
      <Scene3DDomOverlays state={overlayState} />
      <LiveTextOverlays
        liveTextInput={props.liveTextInput}
        inPlaceEdit={props.inPlaceEdit}
      />
      <ArrayRectGripPlaceholders entities={props.entities} />
    </div>
  );
}

